import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User as SupabaseUser, Session } from "@supabase/supabase-js";
import { logEvent } from "@/lib/systemLogger";

export type UserRole = "owner" | "therapist" | "patient";

export interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  onboardingCompleted?: boolean;
}

interface AuthContextType {
  user: User | null;
  supabaseUser: SupabaseUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  signup: (email: string, password: string, fullName: string, role: UserRole, extraData?: Record<string, any>) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  hasRole: (role: UserRole) => boolean;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

async function fetchUserRole(userId: string): Promise<UserRole | null> {
  // A user may legitimately end up with multiple role rows (e.g. when the
  // auth trigger creates a default 'patient' row before the therapist
  // activation flow inserts the 'therapist' row). We must NOT use
  // .maybeSingle() here — it errors when more than one row exists, which
  // would cause therapists to fall back to the patient interface.
  const { data, error } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId);
  if (error) {
    console.warn("[auth] fetchUserRole error", error);
    return null;
  }
  if (!data || data.length === 0) return null;
  const roles = data.map((r) => r.role as UserRole);
  // Priority: owner > therapist > patient
  if (roles.includes("owner")) return "owner";
  if (roles.includes("therapist")) return "therapist";
  if (roles.includes("patient")) return "patient";
  return null;
}

async function fetchProfile(userId: string): Promise<{ full_name: string } | null> {
  const { data, error } = await supabase
    .from("profiles")
    .select("full_name")
    .eq("id", userId)
    .maybeSingle();
  if (error) console.warn("[auth] fetchProfile error", error);
  return data;
}

async function ensureProfileAndRole(supabaseUser: SupabaseUser): Promise<UserRole | null> {
  // Fallback safety: if the auth trigger failed (or didn't run), self-heal here.
  const meta = (supabaseUser.user_metadata || {}) as Record<string, any>;
  const desiredRoleRaw = (meta.role as string) || "patient";
  const desiredRole: UserRole = (["owner", "therapist", "patient"].includes(desiredRoleRaw)
    ? desiredRoleRaw
    : "patient") as UserRole;

  // Ensure profile row exists
  const { data: prof } = await supabase
    .from("profiles")
    .select("id")
    .eq("id", supabaseUser.id)
    .maybeSingle();
  if (!prof) {
    const { error: pErr } = await supabase.from("profiles").insert({
      id: supabaseUser.id,
      full_name: meta.full_name || "",
      phone: meta.phone || "",
    });
    if (pErr) console.warn("[auth] ensureProfile insert error", pErr);
  }

  // Ensure role row exists
  const { error: rErr } = await supabase
    .from("user_roles")
    .insert({ user_id: supabaseUser.id, role: desiredRole });
  if (rErr && !String(rErr.message || "").toLowerCase().includes("duplicate")) {
    console.warn("[auth] ensureRole insert error", rErr);
  }

  return desiredRole;
}

async function buildUser(supabaseUser: SupabaseUser): Promise<User | null> {
  let role = await fetchUserRole(supabaseUser.id);
  if (!role) {
    console.warn("[auth] no role found, attempting self-heal", supabaseUser.id);
    role = await ensureProfileAndRole(supabaseUser);
    if (!role) {
      console.error("[auth] self-heal failed for user", supabaseUser.id);
      return null;
    }
  }
  const profile = await fetchProfile(supabaseUser.id);

  let onboardingCompleted: boolean | undefined = undefined;
  if (role === "therapist") {
    const { data: tp } = await supabase
      .from("therapist_profiles")
      .select("onboarding_completed")
      .eq("user_id", supabaseUser.id)
      .maybeSingle();
    onboardingCompleted = (tp as any)?.onboarding_completed ?? false;
  }

  return {
    id: supabaseUser.id,
    name: profile?.full_name || supabaseUser.user_metadata?.full_name || "",
    email: supabaseUser.email || "",
    role,
    onboardingCompleted,
  };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [supabaseUser, setSupabaseUser] = useState<SupabaseUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Resolvers for login/signup to wait until user state is built
  const pendingAuthResolve = React.useRef<((user: User | null) => void) | null>(null);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      // Log auth lifecycle events (best-effort)
      if (event === "SIGNED_IN" && session?.user) {
        logEvent({ level: "info", category: "auth", event: "login_success", message: "Usuário autenticado", userId: session.user.id, userEmail: session.user.email });
      } else if (event === "SIGNED_OUT") {
        logEvent({ level: "info", category: "auth", event: "logout", message: "Usuário desconectado" });
      } else if (event === "PASSWORD_RECOVERY") {
        logEvent({ level: "info", category: "auth", event: "password_recovery", message: "Recuperação de senha iniciada", userId: session?.user?.id ?? null });
      } else if (event === "USER_UPDATED" && session?.user) {
        logEvent({ level: "info", category: "user", event: "user_updated", message: "Dados do usuário atualizados", userId: session.user.id });
      }
      if (session?.user) {
        setSupabaseUser(session.user);
        setTimeout(async () => {
          const u = await buildUser(session.user);
          setUser(u);
          setIsLoading(false);
          // Resolve pending login/signup promise
          if (pendingAuthResolve.current) {
            pendingAuthResolve.current(u);
            pendingAuthResolve.current = null;
          }
          // Luma: enqueue intro WhatsApp on every login.
          // The dispatch is idempotent (dedupe_key) and luma_intro_sent is updated
          // by a separate flow only when the worker confirms actual delivery — never
          // here, otherwise a failed send would silently block all future retries.
          if (event === "SIGNED_IN" && session.user) {
            setTimeout(async () => {
              try {
                const { data: prof } = await supabase
                  .from("profiles")
                  .select("luma_intro_sent, phone, full_name")
                  .eq("id", session.user.id)
                  .maybeSingle();
                if (!prof) {
                  console.warn("[luma] intro skipped: profile not found");
                  return;
                }
                if (prof.luma_intro_sent) {
                  console.log("[luma] intro already sent, skipping");
                  return;
                }
                if (!(prof.phone || "").trim()) {
                  console.log("[luma] intro deferred: no phone yet");
                  return;
                }
                const { error: dispatchErr } = await supabase.functions.invoke(
                  "luma-dispatch",
                  {
                    body: {
                      event_type: "LUMA_INTRO",
                      user_id: session.user.id,
                      payload: { name: (prof.full_name || "").split(" ")[0] || "" },
                      dedupe_key: `LUMA_INTRO:${session.user.id}`,
                    },
                  },
                );
                if (dispatchErr) {
                  console.warn("[luma] intro dispatch failed", dispatchErr);
                  logEvent({
                    level: "error",
                    category: "luma",
                    event: "luma_intro_dispatch_failed",
                    message: dispatchErr.message || "dispatch error",
                    userId: session.user.id,
                  });
                } else {
                  console.log("[luma] intro enqueued for user", session.user.id);
                  logEvent({
                    level: "info",
                    category: "luma",
                    event: "luma_intro_enqueued",
                    message: "Mensagem de boas-vindas enfileirada",
                    userId: session.user.id,
                  });
                }
              } catch (e) {
                console.warn("[luma] intro dispatch failed", e);
              }
            }, 1500);
          }
        }, 0);
      } else {
        setSupabaseUser(null);
        setUser(null);
        setIsLoading(false);
        if (pendingAuthResolve.current) {
          pendingAuthResolve.current(null);
          pendingAuthResolve.current = null;
        }
      }
    });

    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session?.user) {
        setSupabaseUser(session.user);
        const u = await buildUser(session.user);
        setUser(u);
      }
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const waitForUser = useCallback((): Promise<User | null> => {
    return new Promise((resolve) => {
      pendingAuthResolve.current = resolve;
      // Timeout safety: resolve after 10s even if auth state never fires
      setTimeout(() => {
        if (pendingAuthResolve.current) {
          pendingAuthResolve.current(null);
          pendingAuthResolve.current = null;
        }
      }, 10000);
    });
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      logEvent({ level: "warning", category: "auth", event: "login_failure", message: error.message, payload: { email } });
      return { success: false, error: error.message === "Invalid login credentials" ? "Email ou senha incorretos." : error.message };
    }
    // Wait for onAuthStateChange to build the user before returning
    const builtUser = await waitForUser();
    if (!builtUser) {
      logEvent({ level: "error", category: "auth", event: "profile_load_failed", message: "Erro ao carregar perfil após login", payload: { email } });
      return { success: false, error: "Erro ao carregar perfil do usuário." };
    }
    return { success: true, user: builtUser };
  }, [waitForUser]);

  const signup = useCallback(async (email: string, password: string, fullName: string, role: UserRole, extraData?: Record<string, any>) => {
    console.log("[signup] starting", { email, role });
    // Pass full_name + role + phone via raw_user_meta_data — handle_new_user trigger
    // will create profile + user_roles automatically (works even without active session).
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName,
          role,
          phone: extraData?.phone || "",
        },
        emailRedirectTo: `${window.location.origin}/`,
      },
    });
    if (error) {
      console.error("[signup] supabase error", error);
      logEvent({ level: "warning", category: "auth", event: "signup_failure", message: error.message, payload: { email, role } });
      const msg = error.message || "";
      const lower = msg.toLowerCase();
      if (lower.includes("already") || lower.includes("registered") || lower.includes("exists")) {
        return { success: false, error: "Este email já está cadastrado. Tente fazer login." };
      }
      if (lower.includes("password")) {
        return { success: false, error: "Senha inválida. Use ao menos 6 caracteres." };
      }
      if (lower.includes("email")) {
        return { success: false, error: "Email inválido. Verifique e tente novamente." };
      }
      return { success: false, error: msg };
    }
    if (!data.user) {
      console.error("[signup] no user returned");
      logEvent({ level: "error", category: "auth", event: "signup_no_user", message: "Signup retornou sem usuário", payload: { email } });
      return { success: false, error: "Erro ao criar conta. Tente novamente." };
    }
    logEvent({ level: "info", category: "user", event: "account_created", message: "Conta criada", userId: data.user.id, userEmail: email, payload: { role, needsEmailConfirmation: !data.session } });

    console.log("[signup] user created", { id: data.user.id, hasSession: !!data.session });

    // If session is active (auto-confirm enabled), profile+role were created by trigger.
    // Therapist also needs therapist_profiles row.
    if (data.session) {
      if (role === "therapist" && extraData) {
        const { error: tpError } = await supabase.from("therapist_profiles").insert({
          user_id: data.user.id,
          specialty: extraData.specialty || "",
          bio: extraData.bio || "",
          city: extraData.city || "",
          price_per_session: extraData.price || 0,
        });
        if (tpError) console.warn("[signup] therapist_profiles insert failed", tpError);
      }

      const builtUser = await waitForUser();
      if (!builtUser) {
        console.error("[signup] waitForUser returned null after session");
        return { success: false, error: "Conta criada, mas houve falha ao carregar o perfil. Tente fazer login." };
      }
      return { success: true, user: builtUser };
    }

    // No session => email confirmation required.
    // Profile + role were still created by the auth trigger using raw_user_meta_data.
    console.log("[signup] email confirmation required");
    return {
      success: false,
      error: "Conta criada! Verifique seu email para confirmar o cadastro antes de fazer login.",
      needsEmailConfirmation: true,
    } as any;
  }, [waitForUser]);

  const logout = useCallback(async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSupabaseUser(null);
  }, []);

  const hasRole = useCallback((role: UserRole) => user?.role === role, [user]);

  const refreshUser = useCallback(async () => {
    if (supabaseUser) {
      const u = await buildUser(supabaseUser);
      setUser(u);
    }
  }, [supabaseUser]);

  return (
    <AuthContext.Provider value={{ user, supabaseUser, isAuthenticated: !!user, isLoading, login, signup, logout, hasRole, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
